import { ValidatorFn } from '@angular/forms';

import { IModalDialogOptions } from '@app/shared/types/ui/modal-options.type';

import {
  ITransferCreateFormAddition,
  ITransferCreateOptions,
  IFormInitialData,
} from './tiny-transfer.types';
import {
  BTN_DONE,
  BTN_CANCEL,
} from '@app/text-content/button-templates/button-templates-content';
import { WIDGET_COMPONENT } from '@app/shared/types/enums/widget-component.enum';
import {
  TRANSFER_TYPE_ENUM_OPTIONS,
  TRANSFER_TYPE,
} from '@app/shared/types/entities/transfer/transfer.enums';
import {
  WIDGET_FAMILY,
  MIN_NUMBER_FOR_TRANSFER_QUANTITY,
  MAX_NUMBER_FOR_TRANSFER_QUANTITY,
} from '@config/global-constants';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';
import {
  TOOLBAR_BUTTON_POSITION,
  DROPDOWN_MENU_POSITION,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/toolbar-button.type';
import { CELL_RENDERER } from '@app/shared/types/enums/cell-renderer.enum';
import { ICellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import { ListColumn } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/list-column.type';
import {
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community/dist/lib/entities/colDef';
import { generateDecimalRegexp } from '@app/shared/constants/common-regexps';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import { isNil, find, get, isEmpty } from 'lodash';
import { decimalNumber } from '@app/shared/common/decimal-number/decimal-number';
import { TinyDuringTransferFormService } from './tiny-during-transfer-form.service';
import { XuxGridAction, XUX_GRID_ACTIONS } from '@app/shared/types';
import {
  BTN_REPLENISH_QOH,
  BTN_REPLENISH_ZERO_QOH,
  GROUP_BTN_REPLENISH_BASE,
} from '../transfer-replenish/transfer-replenish.constants';
import { CLEAR_QTY_REQUESTED } from '../transfer/transfer-constants';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import {
  getQuickFilterConfigForScanning,
  getScanningHighlightRowClass,
} from '@app/shared/utils/common-utils';
import { I18n } from '@app/i18n/strings-keys';

export const getTinyDurTransferDefinition = (
  service: TinyDuringTransferFormService,
  options: {
    createOptions: ITransferCreateOptions;
    formInitData: IFormInitialData;
    formAddition: ITransferCreateFormAddition;
    validators?: Record<string, ValidatorFn[]>;
  },
  on?: {
    beforeFormClose?: IModalDialogOptions['beforeFormClose'];
  },
): IModalDialogOptions => {
  const { createOptions, formInitData, formAddition, validators } = options;

  const event = createOptions.event;
  const isTransferIn = createOptions.isTransferIn;
  const quickFilterOptions = getQuickFilterConfigForScanning(false);
  const needEventListener = get(formAddition, 'needEventListener', false);

  return {
    showLoader: true,
    title: service.i18nService.instant(
      I18n.Forms.TransferForm.CreateDuringEventTransfer,
      {
        type: service.i18nService.instant(
          isTransferIn
            ? I18n.Forms.TransferForm.In
            : I18n.Forms.TransferForm.Out,
        ),
      },
    ),
    modalClass: `modal-hg`,
    buttons: [
      {
        ...BTN_DONE,
        caption: I18n.Common.Submit,
      },
      BTN_CANCEL,
    ],
    validation: !isEmpty(validators),
    showPristineError: true,
    schema: {
      type: 'object',
      properties: {
        // need this to keep virtual field position in the modal form
        virtual: {},
        transfer_type: {
          type: 'string',
          enum: ['pre', 'during', 'outside'],
        },
        event_id: {
          type: ['string', 'object'],
        },
        site_id: {
          type: ['string', 'object'],
        },
        site_id_to: {
          type: ['string', 'object'],
        },
        transaction_date: {
          type: ['string', 'object'],
        },
        inventory_items: {
          type: 'array',
        },
      },
    },
    formRules: {
      fields: {
        transfer_type: {
          title: I18n.Common.Type,
          widget: WIDGET_COMPONENT.input_radio,
          optionText: TRANSFER_TYPE_ENUM_OPTIONS,
          widgetFamily: WIDGET_FAMILY.XUX,
          required: true,
          default: TRANSFER_TYPE.DURING_EVENT_TRANSFER,
          show: false,
        },
        event_id: {
          show: false,
          required: true,
          default: event.id,
        },
        'virtual.event_name': {
          title: I18n.EntityNames.Event,
          widgetFamily: WIDGET_FAMILY.XUX,
          disabled: true,
          required: true,
          default: event.name,
        },
        'virtual.transaction_date': {
          show: false,
          default: formInitData.transferDateTimeUTC,
        },
        'virtual.event_type_entity_id': {
          show: false,
        },
        site_id: {
          title: I18n.Common.From,
          widget: isTransferIn
            ? WIDGET_COMPONENT.entity_select
            : WIDGET_COMPONENT.select,
          widgetFamily: WIDGET_FAMILY.XUX,
          required: true,
          trim: true,
          disabled: (): boolean => !isTransferIn,
          default: formInitData.siteId,
          selectOptions: isTransferIn
            ? null
            : [
                {
                  text: formInitData.siteName,
                  value: formInitData.siteId,
                },
              ],
          widgetData: {
            entity: ENTITY_TYPES.SITE,
            dataSource: formAddition.siteDataSource,
            isModal: true,
            lazy: false,
            enableClientFiltering: true,
            artificialInputCheck: true,
          },
        },
        site_id_to: {
          title: I18n.Common.To,
          widget: isTransferIn
            ? WIDGET_COMPONENT.select
            : WIDGET_COMPONENT.entity_select,
          widgetFamily: WIDGET_FAMILY.XUX,
          required: true,
          trim: true,
          disabled: (): boolean => isTransferIn,
          default: formInitData.siteIdTo,
          selectOptions: isTransferIn
            ? [
                {
                  text: formInitData.siteName,
                  value: formInitData.siteIdTo,
                },
              ]
            : null,
          widgetData: {
            entity: ENTITY_TYPES.SITE,
            dataSource: formAddition.siteDataSource,
            isModal: true,
            lazy: false,
            enableClientFiltering: true,
            artificialInputCheck: true,
          },
        },
        transaction_date: {
          title: I18n.Common.Date,
          widget: WIDGET_COMPONENT.xux_datepicker,
          widgetFamily: WIDGET_FAMILY.XUX,
          required: true,
          widgetData: {
            minimumObs: service.transactionDateLimit.minimumObs,
            maximumObs: service.transactionDateLimit.maximumObs,
          },
          disabled: true,
          default: formInitData.transferDate,
        },
        inventory_items: {
          title: I18n.Forms.GridForm.ItemList,
          class:
            'tiny-during-transfer-items-grid toolbar-flex-row navigate-grid',
          widgetFamily: WIDGET_FAMILY.XUX,
          widget: WIDGET_COMPONENT.edit_grid_custom,
          widgetData: {
            showLoader: formAddition.isLoadingItems$,
            gridHeight: '400px',
            hiddenErrorBlock: true,
            useVisibleItemCountDescription: true,
            filterable: true,
            showSearch: true,
            multiselect: false,
            orderControls: false,
            disableColumnSelector: true,
            artificialInputCheckForSearchPanel: true,
            userGridOptions: {
              suppressMovableColumns: true,
              suppressClickEdit: true,
              suppressRowClickSelection: true,
              defaultColDef: {
                editable: true,
              },
              rowClassRules: getScanningHighlightRowClass(
                service.scanningService,
              ),
            },
            inlineEditOptions: {
              beforeActionMappers: [
                service.getInlineChangeMapper(options.createOptions),
              ],
            },
            eventHandlers: {
              [XUX_GRID_ACTIONS.GRID_READY]: (
                action: XuxGridAction,
                grid: XuxEditGridComponent,
              ): void => {
                formAddition.gridApi$.next(action.payload?.gridApi);
                formAddition.itemsGrid$.next(grid);
                service.getTransferGridReadyHandler(action);
              },
            },
            buttons: [
              {
                caption: CLEAR_QTY_REQUESTED,
                class: 'btn btn-default mr-10',
                position: TOOLBAR_BUTTON_POSITION.HEADER,
                click: service.openClearQtyModal.bind(
                  service,
                  I18n.Forms.TransferForm.QtyRequested,
                  ['initial_count', 'initialFactorAndCountValue.initial_count'],
                ),
                disableWithGrid: true,
                disabledObs: formAddition.clearQtyRequestBtnDisable$,
              },
              {
                captionObs: formAddition.showItemsCaption$,
                class: 'btn-primary mr-10',
                position: TOOLBAR_BUTTON_POSITION.HEADER,
                click: (): void => {
                  formAddition.showItemsCaptionChange$.next();
                },
                disableWithGrid: false,
              },
              {
                ...GROUP_BTN_REPLENISH_BASE,
                position: TOOLBAR_BUTTON_POSITION.HEADER,
                class: 'btn-primary np-non-effect',
                dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
                disabledObs: formAddition.replenishButtonDisabled$,
                childItems: [
                  {
                    ...BTN_REPLENISH_QOH,
                    click: (): Promise<void> =>
                      service.onClickReplenish(
                        BTN_REPLENISH_QOH,
                        createOptions,
                        formAddition,
                      ),
                  },
                  {
                    ...BTN_REPLENISH_ZERO_QOH,
                    click: () =>
                      service.onClickReplenish(
                        BTN_REPLENISH_ZERO_QOH,
                        createOptions,
                        formAddition,
                      ),
                  },
                ],
              },
            ],
            columns: [
              {
                path: 'is_chargeable',
                title: '',
                defaultView: true,
                headerComponentParams: {
                  hintOptions: {
                    description: service.i18nService.translate(
                      I18n.InfoMessages.$IndicatesChargeableItemsBrackets,
                    ),
                    placement: 'top',
                  },
                },
                valueGetter: (params: ValueGetterParams): string => {
                  return params.data.is_chargeable ? '$' : '';
                },
                minWidth: 40,
                maxWidth: 40,
              },
              {
                path: 'name',
                title: I18n.Forms.GridForm.ItemName,
                cellClass: 'cell-wrap-text',
                cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
                cellRendererParams: {
                  // turn off the row count
                  suppressCount: true,
                },
                autoHeight: true,
                defaultView: true,
                sortable: false,
                showRowGroup: true,
                filterParams: {
                  quickFilter: true,
                },
                minWidth: 220,
                ...quickFilterOptions,
              },
              {
                path: 'measure_entity_id',
                title: I18n.Forms.TransferForm.TransferUnit,
                defaultView: true,
                editable: true,
                headerComponentParams: {
                  hintOptions: {
                    description: service.i18nService.translate(
                      I18n.Forms.TransferForm.TransferUnitTooltip,
                    ),
                    placement: 'top',
                  },
                },
                cellRenderer: CELL_RENDERER.custom_select_renderer,
                cellRendererParams: (
                  params: ICellRendererParams,
                ): ListColumn['cellRendererParams'] =>
                  service.getMeasureEntityIdCellRendererParams(params),
                cellClass:
                  'cell-price ag-cell-editable-renderer cell-overflow-visible-without-hover',
                minWidth: 120,
                width: 120,
              },
              {
                path: 'initial_count',
                title: I18n.Forms.TransferForm.QtyRequested,
                defaultView: true,
                editable: true,
                valueFormatter: (params: ValueFormatterParams): string =>
                  service.numberFormatService.getNumberFormatter(false)(params),
                cellClass: 'ag-numeric-cell show-placeholder',
                cellRenderer: CELL_RENDERER.float_input_renderer,
                cellRendererParams: (
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  params: ICellRendererParams,
                ): ListColumn['cellRendererParams'] => {
                  return {
                    defaultValue: null,
                    placeholder: '0.00',
                    disableKeyUpFinishEditing: true,
                    min: MIN_NUMBER_FOR_TRANSFER_QUANTITY,
                    max: MAX_NUMBER_FOR_TRANSFER_QUANTITY,
                    acceptableInputRegex: generateDecimalRegexp(
                      service.stateService.getNumberPrecision(),
                      false,
                    ),
                    usePreferenceDecimalPrecision: {
                      overrideMax: true,
                      overrideMin: true,
                    },
                    artificialInputCheck: true,
                  };
                },
                ...COLUMN_ALIGN_RIGHT,
                minWidth: 80,
                width: 80,
              },
              {
                defaultView: true,
                title: I18n.Forms.TransferForm.QtyInBaseUnit,
                path: 'qty_in_each',
                minWidth: 80,
                width: 80,
                valueFormatter: (
                  params: ValueFormatterParams,
                ): string | null => {
                  const initialCount = params.data.initial_count;

                  if (isNil(initialCount)) {
                    return null;
                  }

                  const currentMeasure = find(params.data.measures, {
                    entity_id: params.data.measure_entity_id,
                  });

                  const qtyInEach = decimalNumber(initialCount).mul(
                    currentMeasure.factor_value,
                  );

                  return service.numberFormatService.getNumberFormatter(false)({
                    ...params,
                    value: qtyInEach,
                  });
                },
                ...COLUMN_ALIGN_RIGHT,
              },
              {
                defaultView: true,
                title: I18n.Forms.GridForm.BaseUnit,
                path: 'item_base_unit',
                minWidth: 80,
                width: 80,
              },
            ],
          },
          validators: get(validators, 'inventory_items'),
        },
      },
      defaultGroup: true,
      virtual: {
        type: 'object',
        properties: {
          // use this to catch event type entity id for replenish
          event_type_entity_id: {
            type: 'string',
            default: formInitData.eventTypeEntityId,
          },
          event_name: {
            type: 'string',
          },
          transaction_date: {
            type: 'string',
          },
        },
      },
    },
    onFormAction: service.onFormAction.bind(service),
    onFormReady: service
      .getOnFormReady(formAddition, createOptions)
      .bind(service),
    beforeFormClose: on?.beforeFormClose,
    inlineLabelObs: formAddition.inlineLabel$,
    needEventListener,
  };
};
